/**
 * web-client-app D9 — what every piece's behavior half returns, and how a
 * piece takes its options and reports its events.
 *
 * The standard look (`<Piece>View`) and, from web-studio-sites on, a custom
 * site's own drawing both read `state` and call `actions`; neither holds a rule.
 */
import { createContext, useContext, useMemo, useRef } from 'react';

import type { PieceContract, ResolvedOptions } from './define';
import { resolveOptions } from './options';

export interface PieceRuntime<C extends PieceContract, S, A> {
  readonly state: S;
  readonly actions: A;
  readonly options: ResolvedOptions<C>;
}

/** Reports a piece's events to its host (a page, or a custom site's script). */
export type PieceEventHandler<E> = <K extends keyof E & string>(name: K, payload: E[K]) => void;

export interface PieceInput<E> {
  /** Raw options from the page or the custom site; checked against the contract. */
  readonly options?: Readonly<Record<string, unknown>>;
  readonly onEvent?: PieceEventHandler<E>;
}

/** The piece's options, resolved once per distinct input. */
export function usePieceOptions<C extends PieceContract>(
  contract: C,
  raw: Readonly<Record<string, unknown>> | undefined,
): ResolvedOptions<C> {
  const key = JSON.stringify(raw ?? {});
  // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the serialized options
  return useMemo(() => resolveOptions(contract, raw), [contract, key]);
}

/** A stable emit that always calls the latest handler. */
export function usePieceEmit<E>(onEvent: PieceEventHandler<E> | undefined): PieceEventHandler<E> {
  const ref = useRef(onEvent);
  ref.current = onEvent;
  return useMemo<PieceEventHandler<E>>(() => (name, payload) => ref.current?.(name, payload), []);
}

/** Spread on every drawn element: its published part name. */
export function part(name: string): { 'data-part': string } {
  return { 'data-part': name };
}

/** A part's published states; any text for a component drawn for whichever piece hands it its contract. */
type PartStates<P, K extends keyof P> = string extends keyof P
  ? string
  : P[K] extends { readonly states: readonly (infer S extends string)[] }
    ? S
    : never;

/** What a drawn part carries: `data-part`, and `data-role` and `data-state` when it has them. */
export type PartAttributes = Readonly<Record<string, string>> & { readonly 'data-part': string };

/**
 * web-studio-sites D6: the part helper of one piece, bound to its contract, so
 * a view draws only published parts and states. Each part carries `data-part`,
 * its shared `data-role` as the contract lists it, and `data-state` while it
 * shows one of its listed states (none when the state given is null or false).
 */
export function partsOf<C extends Pick<PieceContract, 'parts'>>(contract: C) {
  return <K extends keyof C['parts'] & string>(name: K, state?: PartStates<C['parts'], K> | null | false): PartAttributes => {
    const role = contract.parts[name]?.role;
    return { 'data-part': name, ...(role ? { 'data-role': role } : {}), ...(state ? { 'data-state': state } : {}) };
  };
}

/**
 * web-studio-sites D6: spread on a piece's root element, on Telify's pages as in
 * a placeholder, so `[data-telify="services"] [data-part="price"]` reaches one piece.
 */
export function pieceRoot(contract: Pick<PieceContract, 'name'>): { 'data-telify': string; 'data-telify-root': '' } {
  return { 'data-telify': contract.name, 'data-telify-root': '' };
}

const PlacedContext = createContext(false);

/**
 * web-studio-sites D4: set by the kit around a piece it places on a custom page.
 * A placed piece fits the site's page: no section band, no frame width, no
 * section padding, no fixed anchors. A host flag, not an option, so the
 * contract does not change.
 */
export const PlacedProvider = PlacedContext.Provider;

/** True when the kit placed this piece on a custom page. */
export function usePlaced(): boolean {
  return useContext(PlacedContext);
}
